/**
 * Every line of text on the investor's startup panel, startup page, follow windows and Search, in one
 * place so any line can be changed without touching a component (handoff D6). Keys are dot-case,
 * `{name}` is a variable. Text from the prototype is verbatim, curly apostrophes included; lines the
 * prototype does not have are written in its voice and listed in `OURS` so David can see which to approve.
 * British spelling; no dashes as punctuation; no repeating the privacy line after the fact.
 */
export const COPY = {
  // ── The panel ─────────────────────────────────────────────────────────────
  'panel.aria.details': '{startup} details',
  'panel.fromSearch': 'From your search',
  'search.name.profile': 'Open profile',
  'search.name.notes': 'Your notes',
  'search.name.deck': 'Deck',
  'search.name.documents': '{n} documents',
  'search.name.update.one': '1 email update',
  'search.name.update.many': '{n} email updates',
  'invite.creating': 'Creating the invite…',
  'invite.retry': 'Try again',
  'search.name.latest': 'Latest from the founder',
  'panel.matched.heading': 'What matched your search',
  'panel.aria.close': 'Close',
  'panel.aria.more': 'More for {startup}',
  'panel.openProfile': 'Open profile',
  'panel.noLink': 'This profile is private, so it cannot be opened here.',
  'panel.loading': 'Loading {startup}',
  'panel.error': 'Could not load this startup.',
  'panel.retry': 'Try again',
  'panel.refused':
    'We couldn’t find an available profile for this startup. The profile may be private.',

  // ── Menus ─────────────────────────────────────────────────────────────────
  'menu.openProfile': 'Open profile',
  'menu.editFollow': 'Edit follow',
  'menu.editFollow.hint': 'Change what you want to hear about.',
  'menu.stopFollowing': 'Stop following',
  'row.why.material':
    'You follow every material update: new funding, key hires, a pivot or a big milestone.',
  'row.why.condition':
    'You follow for the condition you set. Alerts come when a dated founder update meets it.',
  'row.savedRead': 'Saved read',
  'row.savedRead.title': 'Saved read: your AI has saved an assessment of this startup.',
  'menu.stopFollowing.hint': 'Alerts about {startup} stop.',
  'menu.writeNote': 'Write a note',
  'menu.writeNote.hint': 'For your own thoughts or notes from a call.',
  'menu.getIntro': 'Get intro',
  'menu.getIntro.hint':
    'Send the founders a note with your investor profile. Replies go to your email.',

  // ── Following for ─────────────────────────────────────────────────────────
  'card.following.title': 'Following for',
  'card.following.chip.active': 'Active',
  'card.following.chip.met': 'Condition met',
  'card.following.chip.waiting': 'Waiting',
  'card.following.chip.waitingSlot': 'Waiting for a free place',
  'card.following.chip.ended': 'Ended',
  'card.following.materialUpdates': 'Material updates',
  'card.following.since': 'Since {date}',
  'card.following.edit': 'Edit follow',
  'card.met.posted': 'What the founder posted',
  'card.met.source': '{label} · dated {date} · added {added}',
  'card.met.open': 'Open the update',
  'card.met.all': 'All updates',
  'card.met.continues': 'Following continues with material updates.',
  'card.waitCheck.label': 'Waiting for your account check',
  'card.waitCheck.body': 'Your follow starts once we have checked your account.',
  'card.waitPlace.label': 'Waiting for a free slot',
  'card.waitPlace.body':
    'Your {plan} plan has {limit} slots and {used} are in use. This follow starts when a slot is free: choose a bigger plan or stop following a startup.',
  'card.waitPlace.bodyLargest':
    'Your {plan} plan has {limit} slots and {used} are in use. This follow starts when a slot is free: stop following a startup.',
  'card.waitPlace.cta': 'See plans',
  'card.waiting.cancel': 'Cancel follow',
  'card.ended.title': 'Follow ended by the founder',
  'card.ended.body':
    'The founder of {startup} ended your follow on {date}, so alerts about {startup} have stopped.',
  'card.ended.back': 'Back to Following',

  // ── Not following ─────────────────────────────────────────────────────────
  'card.nofollow.title': 'Follow, so you don’t miss the progress you’re waiting for',
  'card.nofollow.line1':
    'Hear the day {startup} posts it: any material update, or the condition you set.',
  'card.nofollow.line2':
    'Your record starts the day you follow: every update, dated, with its source.',
  'card.nofollow.line3': 'Founders see that you follow. Your condition stays private.',
  'card.allowance.free': '{used} of {limit} free follows used',
  'card.allowance.paid': '{used} of {limit} slots used',
  'card.latest.title': 'Latest from {startup}',

  // ── Since you followed ────────────────────────────────────────────────────
  'card.since.title': 'Since you followed',
  'card.since.from': 'From {date}',
  'card.since.entry': '{date} · {kind}',
  'card.since.viewSource': 'View source',
  'card.since.sourceGone': 'Source no longer available',
  'card.since.empty': 'No updates since you followed.',
  'card.since.kind.deck': 'New deck',
  'card.since.kind.document': 'New document',
  'card.since.kind.update': 'Email update',
  'card.since.kind.session': 'Recorded session',

  // ── Your AI's assessment ──────────────────────────────────────────────────
  'ai.title': 'Your AI’s assessment',
  'ai.private': 'Private',
  'ai.savedBy': 'Saved by {client} · {date} · version {version}',
  'ai.savedByWithPass': 'Saved by {client} with your pass · {date}',
  'ai.moved': 'Saved before {startup}’s update of {date}.',
  'ai.mainRisk': 'Main risk',
  'ai.byArea': 'By area',
  'ai.area': '{area} · {finding}',
  'ai.wouldChange': 'What would change it',
  'ai.readFull': 'Read full assessment',
  'ai.followRow': 'Don’t miss the day {startup} posts {ringOn}.',
  'ai.follow': 'Follow',
  'ai.empty':
    'Connect your AI and its assessment of {startup} will appear here: the main risk, each area with its source, and what would change its mind.',
  'ai.connect': 'Connect your AI',
  'win.title': 'Your AI’s assessment of {startup}',
  'win.close': 'Close',
  'win.decision': 'Decision',
  'win.following': 'Following',
  'win.followingSince': 'since {date}',
  'win.conditionMet': 'Condition met',
  'win.passed': 'Passed on {date} · {reason}',
  'win.revisit': 'Would look again when: {revisit}',
  'win.noDecision': 'No decision yet',
  'win.ringOn': 'The update your AI would wait for',
  'win.changeMind': 'Would change its mind:',
  'win.noSource': 'No dated line on the record',
  'win.notSaved': 'Not saved',
  'win.otherAreas': 'Other areas your AI named',
  'win.outside': 'From outside SeedForge',
  'win.notes': 'Your AI’s notes',
  'win.basis.on_record': 'On the record',
  'win.basis.outside_record': 'Outside SeedForge',
  'win.basis.own_calculation': 'Own calculation',
  'win.basis.mixed': 'Mixed sources',
  'win.basis.unstated': 'Basis not stated',
  'verdict.strong': 'Strong',
  'verdict.adequate': 'Adequate',
  'verdict.weak': 'Weak',
  'verdict.not_on_record': 'Not on the record',

  // ── Your note ─────────────────────────────────────────────────────────────
  'note.title': 'Your note',
  'note.edited': 'Edited {date}',
  'note.edit': 'Edit note',
  'note.placeholder': 'Your thoughts, or notes from a call',
  'note.consent': 'Founders never see your note.',
  'note.cancel': 'Cancel',
  'note.save': 'Save',
  'note.aria': 'Your note',

  // ── Passed ────────────────────────────────────────────────────────────────
  'pass.title': 'You passed on {date}',
  'pass.reason': 'Reason:',
  'pass.revisit': 'Would look again when:',
  'pass.undo': 'Undo pass',
  'pass.outcome.not_a_fit': 'Not a fit',
  'pass.outcome.too_early': 'Too early',
  'pass.outcome.not_deploying': 'Not investing now',
  'pass.outcome.none': 'No reason given',

  // ── Page header ───────────────────────────────────────────────────────────
  'page.back': 'Back to Following',
  'page.backSearch': 'Back to Search',
  'page.follow': 'Follow',
  'page.following': 'Following',
  'page.waiting': 'Waiting',
  'page.ended': 'Follow ended',
  'page.updated': 'Updated {date}',

  // ── Follow window ─────────────────────────────────────────────────────────
  'follow.title': 'Follow {startup}',
  'follow.sub': 'Get alerts in the app and by email.',
  'follow.material': 'Material updates',
  'follow.material.hint': 'New funding, key hires, a pivot or a big milestone.',
  'follow.condition': 'Set a condition',
  'follow.condition.hint':
    'Tell us what you are waiting for, in your own words, for example “their first paying customer”.',
  'follow.tellMe': 'Tell me when',
  'follow.placeholder': 'for example, they sign their first paying customer',
  'follow.suggested': 'Suggested by your AI',
  'follow.consent': 'The founder sees your name, title and firm. Your condition stays private.',
  'follow.submit': 'Follow',
  'follow.needText': 'Add what you are waiting for to follow with a condition.',
  'follow.edit.title': 'Edit follow',
  'follow.edit.save': 'Save',
  'follow.edit.cancel': 'Cancel',
  'follow.close': 'Close',

  // ── Stop and cancel ───────────────────────────────────────────────────────
  'stop.title': 'Stop following {startup}?',
  'stop.body': 'You will not hear from {startup} any more. Your notes and passes stay.',
  'stop.cancel': 'Cancel',
  'stop.confirm': 'Stop following',
  'cancelFollow.title': 'Cancel your follow of {startup}?',
  'cancelFollow.body':
    'It has not started, so the founder has not seen it. You can follow again at any time.',
  'cancelFollow.keep': 'Keep waiting',
  'cancelFollow.confirm': 'Cancel follow',

  // ── Get intro ─────────────────────────────────────────────────────────────
  'intro.title': 'Get an intro to {startup}',
  'intro.desc':
    'We forward your note to the founders with your investor profile. Replies go to your email.',
  'intro.label': 'Your message',
  'intro.tip': 'Tip: name one thing from the deck that caught your eye.',
  'intro.cancel': 'Cancel',
  'intro.send': 'Send intro request',

  // ── Search ────────────────────────────────────────────────────────────────
  'search.title': 'Search',
  'search.subtitle': 'Find startups through what founders have shared.',
  'search.aria.box': 'Search startups on SeedForge',
  'search.aria.submit': 'Search',
  'search.placeholder': 'Search by startup name',
  'search.sort': 'Newest first',
  'search.sort.name': 'Name A to Z',
  'search.heading': 'All startups on SeedForge',
  'search.results': 'Results for “{query}”',
  'search.recent': 'Recent searches',
  'search.connect': 'Also inside Claude or ChatGPT.',
  'search.chip.all': 'All',
  'search.chip.week': 'Joined this week',
  'search.chip.raising': 'Raising now',
  'search.chip.europe': 'Europe',
  'search.chip.focus': 'Fits your focus',
  'search.status.watchdog': 'Watchdog match',
  'search.th.startup': 'Startup',
  'search.th.round': 'Round',
  'search.th.sector': 'Sector',
  'search.th.joined': 'Joined',
  'search.th.status': 'Status',
  'search.th.actions': 'Actions',
  'search.row': '{oneLine} · {city}',
  'search.status.following': 'Following',
  'search.status.waiting': 'Waiting',
  'search.status.passed': 'Passed',
  'search.count': 'Showing {shown} of {total}',
  'search.count.all': 'Showing all {total}',
  'search.count.scroll': 'Showing {shown} of {total} · the list continues as you scroll',
  'search.more': 'Show more',
  'search.aria.clear': 'Clear search',
  'search.loading': 'Loading startups…',
  'search.error': 'Could not load startups.',
  'search.empty.name': 'We couldn’t find an available profile for {query}.',
  'search.empty.filters': 'No startups match these filters.',
  'search.clearFilters': 'Clear filters',
  'search.phrase.label': 'Search what founders have shared',
  'search.phrase.subtitle': 'Find startups through what founders have shared.',
  'search.new': 'New search',
  'search.phrase.placeholder': 'A startup’s name, or what you look for',
  'search.input.label': 'Search startups on SeedForge',
  'search.browseAll': 'Browse all {n} startups',
  'search.browseAll.plain': 'Browse all startups',
  'search.suggest.starters': 'Starting points',
  'search.recent.empty': 'Your searches appear here.',
  'search.browse.back': 'Back to search',
  'search.phrase.submit': 'Search',
  'search.phrase.count': 'Matching passages found in {matched} of {of} {profiles}{region}.',
  'search.profiles.both': 'pre-seed and seed profiles',
  'search.profiles.seed': 'seed profiles',
  'search.profiles.preseed': 'pre-seed profiles',
  'search.profiles.any': 'startups',
  'search.region.in': 'in {region}',
  'search.phrase.countOne': 'Matching passages found in 1 startup.',
  'search.phrase.refine': 'Refine these results, or ask something new',
  'search.sort.latest': 'Latest update first',
  'search.focus.edit': 'Edit before saving',
  'search.tabs.label': 'Search views',
  'search.tabs.search': 'Search',
  'watchdog.tab': 'Discovery watchdog',
  'watchdog.new': '{n} new',
  'watchdog.on': 'On · Finds new startups that fit what you look for.',
  'watchdog.paused': 'Paused · Nothing new is looked for until you resume.',
  'watchdog.pause': 'Pause',
  'watchdog.resume': 'Resume',
  'watchdog.criteria.heading': 'Looking for',
  'watchdog.criteria.focus': 'From your saved focus',
  'watchdog.criteria.learned': 'Learned from your searches',
  'watchdog.criteria.manual': 'Added by you',
  'watchdog.remove': 'Remove {what}',
  'watchdog.matches.heading': 'Matches · {n}',
  'watchdog.focus.label': 'Your focus',
  'watchdog.focus.edit': 'Edit focus',
  'watchdog.leaveOut.label': 'Leaving out',
  'watchdog.focus.lock': 'Keep {what} in your focus',
  'watchdog.learned.label': 'Learned from your searches, here and through your AI',
  'watchdog.learned.empty': 'Nothing learned yet.',
  'watchdog.learned.note': 'Removing one leaves your focus as it is.',
  'watchdog.new.heading': 'New since your last visit · {n}',
  'watchdog.all.heading': 'All matches · {n}',
  'watchdog.matches.focus': 'Matches your focus ({reasons})',
  'watchdog.matches.interest': 'your interest in {phrases}',
  'watchdog.matches.only': 'Matches {what}',
  'watchdog.all.matches': 'Matches: {reasons}',
  'watchdog.showAll': 'Show all',
  'watchdog.footnote':
    'Email at most once a week, only in a week with new matches. The watchdog never follows a startup for you, and founders never see it.',
  'watchdog.all.joined': 'Joined {date}',
  'watchdog.matches.empty': 'Nothing matches yet. The watchdog looks again each night.',
  'watchdog.error': 'That did not save. Try again.',
  'watchdog.box.open': 'Open the watchdog',
  'watchdog.removed': 'Removed {what}.',
  'watchdog.undo': 'Undo',
  'search.closest.heading': 'Closest searches',
  'search.closest.region': 'Any region in {region}',
  'search.closest.phrase': '“{value}”',
  'search.phrase.empty': 'No profile on SeedForge matches all of this yet.',
  'search.pending.title': 'Your account check is in progress.',
  'search.pending.body':
    'Your search is saved. Quotes from the founders appear here when your account check is complete.',
  'search.name.describe': 'Describe what it does and SeedForge will look for startups like it.',
  'search.name.byWhat': 'Search by what it does',
  'share.linkedin': 'LinkedIn',
  'share.slack': 'Slack',
  'share.titleFor': 'Share {startup}',
  'share.lead': 'We drafted the message. Change anything, then choose where to send it.',
  'share.note.include': 'Include my note',
  'share.audience.label': 'Who is it for',
  'share.audience.colleague': 'A colleague',
  'share.audience.investor': 'Another investor',
  'share.team.line':
    'Working on this with a colleague? Add them to your team to share one watchlist. Team access comes with the 25-follow plan.',
  'share.team.plans': 'See plans',
  'share.message.label': 'Message',
  'pass.cancel': 'Cancel',
  'search.phrase.countPending': '{matched} {profiles}{region} may match.',
  'search.limit.placeholder': 'Search again on {when}',
  'search.phrase.error': 'The search did not run. Try again.',
  'search.searching': 'Searching...',
  'search.phrase.passed': 'Passed',
  'search.criteria.label': 'Searching for',
  'search.criteria.preseed': 'Pre-seed',
  'search.criteria.seed': 'Seed',
  'search.criteria.remove': 'Remove {chip}',
  'search.criteria.phrase': 'mentions “{text}”',
  'search.next.heading': 'Change the search',
  'search.next.only': 'Only {stage}',
  'search.next.anyStage': 'Any stage',
  'search.next.removePhrase': 'Remove “{text}”',
  'search.next.remove': 'Remove {chip}',
  'search.watch.now': 'Your watchdog now watches for {what} and will tell you when one joins.',
  'search.opinion.intro':
    'Here are the {matched} {profiles}{region} on SeedForge, with the founders’ own evidence. What would you like to narrow by?',
  'search.watch.updated': 'Your watchdog criteria were updated: {what}.',
  'search.watch.view': 'View criteria',
  'search.watch.undo': 'Undo',
  'search.result.follow': 'Follow',
  'search.result.more': 'More actions for {name}',
  'search.result.open': 'Open',
  'search.recent.count': '{n} results',
  'search.recent.one': '1 result',
  'search.recent.today': 'Today',
  'search.recent.yesterday': 'Yesterday',
  'search.restore.error': 'That search is no longer available.',
  'search.private.note':
    'Your searches stay private and are not shared with anyone outside SeedForge.',
  'search.limit.reached':
    'Today’s search limit reached. Search again tomorrow from 01:00. Your results stay here.',
  'search.opinion.narrow': 'Narrow by',
  'search.focus.question': 'Use this search as your focus?',
  'search.focus.describe': 'Suggestions and your watchdog will start from it: {summary}.',
  'search.focus.use': 'Use as my focus',
  'search.focus.notNow': 'Not now',
  'search.suggest.heading': 'Try a search',
  'search.suggest.focus': 'From your saved focus',
  'search.suggest.count': '{n}',
  'search.result.pass': 'Pass privately',
  'search.result.share': 'Share startup',
  'search.result.passed': 'Passed',
  'search.result.undo': 'Undo',
  'pass.window.title': 'Pass on {name} privately',
  'pass.privacy':
    'The founder never learns who passed, and saving a pass does not start a follow or alerts.',
  'pass.reason.notAFit': 'Not a fit',
  'pass.reason.tooEarly': 'Too early',
  'pass.reason.smallTraction': 'Small traction',
  'pass.reason.notInvesting': 'Not investing now',
  'pass.lookAgain.label': 'Would look again when, optional',
  'pass.reason.legend': 'Reason, optional',
  'pass.offer.written': 'Don’t miss the change you’re waiting for.',
  'pass.offer.tooEarly': 'Don’t miss the progress you’re waiting for.',
  'pass.offer.traction': 'Don’t miss the traction you’re waiting for.',
  'pass.followInstead': 'Follow instead',
  'pass.save': 'Save pass',
  'share.title': 'Share startup',
  'share.privacy': 'Who shared it stays private.',
  'share.email': 'Email',
  'share.whatsapp': 'WhatsApp',
  'share.telegram': 'Telegram',
  'share.copyMessage': 'Copy message',
  'share.sendWith': 'Send with',
  'share.copyLink': 'Copy link',
  'share.copied': 'Link copied',
  'share.noLink': 'You hold no link to this startup, so there is nothing to share yet.',
  'share.error': 'The message could not be drafted. Try again.',
  'search.private': 'This profile is private.',

  // ── Toasts ────────────────────────────────────────────────────────────────
  'toast.follow.active': 'You follow {startup}.',
  'toast.follow.waitingCheck': 'Saved. Your follow starts once we have checked your account.',
  'toast.follow.waitingPlace': 'Saved. Your follow starts when a slot is free.',
  'toast.follow.error': 'We could not follow {startup}. Try again in a moment.',
  'toast.edit.saved': 'Follow updated.',
  'toast.edit.error': 'We could not save your change. Try again in a moment.',
  'toast.stop.done': 'You stopped following {startup}.',
  'toast.stop.error': 'We could not stop following {startup}. Try again in a moment.',
  'toast.cancel.done': 'Follow cancelled.',
  'toast.note.saved': 'Note saved.',
  'toast.note.cleared': 'Note removed.',
  'toast.note.error': 'We could not save your note. Try again in a moment.',
  'toast.undoPass.done': 'Pass undone.',
  'toast.undoPass.error': 'We could not undo the pass. Try again in a moment.',
  'toast.intro.sent': 'Your request is on its way to {startup}.',
  'toast.intro.error': 'We could not send your request. Try again in a moment.',

  // ── extras: follow cards ──────────────────────────────────────────────────
  'card.plan.free': 'Free',
  'card.plan.standard_25': '25-follow',
  'card.plan.standard_100': '100-follow',
  'card.plan.founding': 'Founding',
  // ── extras: following rows ────────────────────────────────────────────────
  'card.following.materialNow': 'Now following material updates',
  'row.active': 'Active',
  'row.waitingCheck': 'Waiting for your account check',
  'row.waitingPlace': 'Waiting for a place',
  'row.waitingPlace.title': 'All the slots on your plan are in use. See plans',
  'row.endedByFounder': 'Ended by founder',
  'row.throughAi': 'through your AI',
  'row.freeInvite': 'Free through your invite',
  'row.updates.none': 'Following since {date}',
  // ── invites: founders who are not on SeedForge yet ───────────────────────
  'invite.strip.text': 'Want to track a startup that isn’t on SeedForge? Invite the founder.',
  'invite.strip.note': 'Startups that join through your invite never count toward your limit.',
  'invite.strip.button': 'Invite a founder',
  'invite.title': 'Invite a founder',
  'invite.field.startup': 'Startup',
  'invite.lead': 'We drafted the message. Change anything, then choose where to send it.',
  'invite.message.label': 'Message',
  'invite.link.label': 'Invite link',
  'invite.copyLink': 'Copy link',
  'invite.copyMessage': 'Copy message',
  'invite.copied': 'Copied',
  'invite.quiet':
    'When they post their deck, we tell you that day and you follow them for free, with every update from their first day.',
  'invite.submit': 'Create invite',
  'invite.cancel': 'Cancel',
  'invite.ready.title': 'Invite ready',
  'invite.ready.body':
    'When {startup} joins through your link and posts their deck, we tell you that day and you follow them for free.',
  'invite.ready.where': 'You’ll find it under Invites in Following.',
  'invite.ready.done': 'Done',
  'invite.ready.open': 'Open Following',
  'invite.duplicate': 'An invite for {startup} already exists.',
  'invite.error': 'We could not create the invite. Try again.',
  'invite.remove': 'Remove invite',
  'invite.removeError': 'We could not remove the invite. Try again.',
  'invite.draft':
    'Hi, I’d like to keep track of {startup}’s progress. Could you join SeedForge through my link and upload your deck? I’ll hear the day it’s up and follow your progress from there.',
  'invites.heading': 'Invites',
  'invites.hide': 'Hide',
  'invites.show': 'Show',
  'invites.status.waiting': 'Waiting for the founder · Invited {date}',
  'invites.status.joined': 'Joined, waiting for their deck',
  'invites.status.removed': 'Ended by founder',
  'invites.reinvite': 'Remove and invite again',
  'invites.open': 'Open invite',
  'invites.remove': 'Remove invite',
  'search.invite.heading': 'Want to track {startup}?',
  'search.invite.body':
    'Send the founder your invite link. We tell you the day they post their deck, and once they join, following them never counts toward your limit.',
  'search.invite.button': 'Invite {startup}’s founder',
  'notify.joinedInvite.label': 'Joined through your invite',
  'notify.joinedInvite.body': 'Their deck is up, and you now follow them for free.',
  // ── extras: assessment ────────────────────────────────────────────────────
  'win.viewSource': 'View source',
} as const

export type CopyKey = keyof typeof COPY

/** The lines the prototype does not have: written in its voice, for David to approve or change. */
export const OURS: ReadonlySet<CopyKey> = new Set<CopyKey>([
  'search.sort.name',
  'row.active',
  'row.waitingCheck',
  'row.waitingPlace',
  'row.waitingPlace.title',
  'row.endedByFounder',
  'row.throughAi',
  'row.freeInvite',
  'panel.noLink',
  'panel.loading',
  'panel.error',
  'panel.retry',
  'panel.refused',
  'card.waitPlace.bodyLargest',
  'card.allowance.paid',
  'card.since.kind.deck',
  'card.since.kind.session',
  'pass.outcome.none',
  'page.backSearch',
  'follow.needText',
  'stop.title',
  'stop.body',
  'cancelFollow.title',
  'cancelFollow.body',
  'cancelFollow.keep',
  'cancelFollow.confirm',
  'search.subtitle',
  'search.placeholder',
  'search.loading',
  'search.error',
  'search.empty.filters',
  'search.clearFilters',
  'search.private',
  'search.results',
  'search.count.all',
  'search.more',
  'search.aria.clear',
  'row.updates.none',
  'toast.follow.active',
  'toast.follow.waitingCheck',
  'toast.follow.waitingPlace',
  'toast.follow.error',
  'toast.edit.saved',
  'toast.edit.error',
  'toast.stop.done',
  'toast.stop.error',
  'toast.cancel.done',
  'toast.note.saved',
  'toast.note.cleared',
  'toast.note.error',
  'toast.undoPass.done',
  'toast.undoPass.error',
  'toast.intro.sent',
  'toast.intro.error',
  'win.basis.unstated',
])

type Vars<K extends CopyKey> = (typeof COPY)[K] extends infer T
  ? T extends string
    ? VarsOf<T>
    : never
  : never
type VarsOf<S extends string> = S extends `${string}{${infer V}}${infer Rest}`
  ? { [P in V | keyof VarsOf<Rest>]: string | number }
  : Record<never, never>

/** The text for a key, with its `{variables}` filled; a missing variable is a bug and throws outside production. */
export function copy<K extends CopyKey>(
  key: K,
  ...args: keyof Vars<K> extends never ? [] : [vars: Vars<K>]
): string {
  const vars = (args[0] ?? {}) as Record<string, string | number>
  return COPY[key].replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = vars[name]
    if (value === undefined) {
      if (import.meta.env.PROD) return ''
      throw new Error(`copy("${key}") is missing {${name}}`)
    }
    return String(value)
  })
}

/** Finding labels, keyed `area.kind` (the same kind repeats across areas). */
export const FINDING_LABELS: Record<string, string> = {
  'team.solo_founder': 'A solo founder',
  'team.missing_technical_founder': 'No technical founder',
  'team.part_time_founders': 'Part-time founders',
  'team.no_domain_experience': 'No domain experience',
  'team.thin_for_stage': 'Thin for the stage',
  'team.strong_domain_fit': 'Strong domain fit',
  'team.complete_for_stage': 'Complete for the stage',
  'market.too_small': 'Market too small',
  'market.unclear_sizing': 'Sizing unclear',
  'market.timing_unclear': 'Timing unclear',
  'market.crowded': 'Crowded market',
  'market.large_and_clear': 'Large and clear',
  'market.niche_but_defensible': 'Niche but defensible',
  'product.idea_only': 'Idea only',
  'product.prototype': 'A prototype',
  'product.live_with_users': 'Live with users',
  'product.live_in_production': 'Live in production',
  'product.differentiated': 'Differentiated',
  'product.undifferentiated': 'Not differentiated',
  'traction.none': 'No traction shown yet',
  'traction.pilot_unpaid': 'Pilots, unpaid',
  'traction.pilot_paid': 'Pilots, paid',
  'traction.paying_customers': 'Paying customers',
  'traction.recurring_revenue': 'Recurring revenue',
  'traction.projections_only': 'Projections only',
  'moat.none_visible': 'No moat visible',
  'moat.data': 'Data advantage',
  'moat.network_effects': 'Network effects',
  'moat.switching_costs': 'Switching costs',
  'moat.regulatory': 'Regulatory protection',
  'moat.distribution': 'Distribution advantage',
  'moat.technology': 'Technology advantage',
  'moat.brand': 'Brand',
  'gtm.channel_unproven': 'Channel unproven',
  'gtm.pricing_unproven': 'Pricing unproven',
  'gtm.sales_motion_mismatch': 'Sales motion mismatch',
  'gtm.founder_led_only': 'Founder-led sales only',
  'gtm.partner_dependent': 'Dependent on partners',
  'gtm.clear_and_working': 'Clear and working',
  'unit_economics.unknown': 'Not shown',
  'unit_economics.negative': 'Negative margins',
  'unit_economics.marginal': 'Marginal',
  'unit_economics.positive': 'Positive',
  'unit_economics.projections_only': 'Projections only',
  'competition.not_addressed': 'Not addressed',
  'competition.incumbents_strong': 'Strong incumbents',
  'competition.fragmented': 'Fragmented field',
  'competition.clear_positioning': 'Clear positioning',
  'regulatory.not_addressed': 'Not addressed',
  'regulatory.path_stated': 'Path stated',
  'regulatory.approval_needed': 'Approval needed',
  'regulatory.not_applicable': 'Not applicable',
  'round.size_mismatch': 'Size does not fit',
  'round.stage_mismatch': 'Stage does not fit',
  'round.terms_unclear': 'Terms unclear',
  'round.fits': 'Fits',
}

export const AREA_LABELS: Record<string, string> = {
  team: 'Team',
  market: 'Market',
  product: 'Product',
  traction: 'Traction',
  moat: 'Moat',
  gtm: 'Go-to-market',
  unit_economics: 'Unit economics',
  competition: 'Competition',
  regulatory: 'Regulatory',
  round: 'Round',
}

const sentence = (text: string) => {
  const spaced = text.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** The label for a finding, or the kind in plain words when a new kind has no label yet. */
export const findingLabel = (area: string, kind: string): string =>
  FINDING_LABELS[`${area}.${kind}`] ?? sentence(kind)

export const areaLabel = (area: string): string => AREA_LABELS[area] ?? sentence(area)
